<template>
  <div class="new-ticket-panel">
    <div class="new-ticket-panel__header">
      <span class="new-ticket-panel__title">Nueva solicitud</span>
      <div class="new-ticket-panel__mode-toggle">
        <button class="new-ticket-panel__mode-btn" :class="{ 'new-ticket-panel__mode-btn--active': mode === 'quick' }" @click="mode = 'quick'">Creación rápida</button>
        <button class="new-ticket-panel__mode-btn" :class="{ 'new-ticket-panel__mode-btn--active': mode === 'full' }" @click="mode = 'full'">Creación completa</button>
      </div>
      <button class="new-ticket-panel__close-btn" @click="onClose"><span class="mdi mdi-close"></span></button>
    </div>

    <!-- Barra de progreso (solo modo completo) -->
    <div v-if="mode === 'full'" class="new-ticket-panel__steps">
      <div v-for="(s, i) in STEPS" :key="s.key" class="new-ticket-panel__step" :class="{ 'new-ticket-panel__step--active': stepIndex === i, 'new-ticket-panel__step--done': stepIndex > i }">
        <span class="new-ticket-panel__step-circle" @click="stepIndex > i && (stepIndex = i)">
          <span v-if="stepIndex > i" class="mdi mdi-check"></span>
          <span v-else>{{ i + 1 }}</span>
        </span>
        <span class="new-ticket-panel__step-label">{{ s.label }}</span>
        <div v-if="i < STEPS.length - 1" class="new-ticket-panel__step-line"></div>
      </div>
    </div>

    <div class="new-ticket-panel__content">
      <div v-if="errorMsg" class="new-ticket-panel__error">{{ errorMsg }}</div>

      <!-- ============ MODO RÁPIDO ============ -->
      <template v-if="mode === 'quick'">
        <section class="new-ticket-panel__section">
          <span class="new-ticket-panel__section-title">Nave e inquilino</span>
          <PlotTenantSearch v-model="selectedPlot" :park-id="parkId" />
        </section>

        <section class="new-ticket-panel__section">
          <span class="new-ticket-panel__section-title">Descripción del problema</span>
          <textarea v-model="description" class="new-ticket-panel__textarea" rows="3" placeholder="Describe el problema brevemente..."></textarea>
          <input ref="quickFileEl" type="file" accept="image/*" multiple style="display: none" @change="onFilesSelected" />
          <button class="new-ticket-panel__attach-btn" @click="quickFileEl?.click()"><span class="mdi mdi-paperclip"></span> Adjuntar fotos</button>
          <div v-if="photos.length" class="new-ticket-panel__photo-previews">
            <div v-for="(p, i) in photos" :key="i" class="new-ticket-panel__photo-preview">
              <img :src="p.previewUrl" />
              <button class="new-ticket-panel__photo-remove" @click="photos.splice(i, 1)"><span class="mdi mdi-close"></span></button>
            </div>
          </div>
        </section>

        <section class="new-ticket-panel__section">
          <div class="new-ticket-panel__row-2col">
            <div>
              <span class="new-ticket-panel__section-title">Prioridad *</span>
              <div class="new-ticket-panel__pills">
                <button v-for="p in PRIORITY_OPTIONS" :key="p.value" class="new-ticket-panel__pill" :class="{ 'new-ticket-panel__pill--active': priority === p.value }" :style="pillStyle(p.color, priority === p.value)" @click="priority = p.value">
                  {{ p.label }}
                </button>
              </div>
            </div>
            <div>
              <span class="new-ticket-panel__section-title">Tipo</span>
              <div class="new-ticket-panel__pills">
                <button v-for="t in TYPE_OPTIONS" :key="t.value" class="new-ticket-panel__pill" :class="{ 'new-ticket-panel__pill--active': type === t.value }" @click="type = t.value">
                  {{ t.label }}
                </button>
              </div>
            </div>
          </div>
        </section>

        <section class="new-ticket-panel__section">
          <span class="new-ticket-panel__section-title">Técnico (opcional)</span>
          <TechnicianChipRow v-model="technicianId" :options="technicianOptions" />
        </section>
      </template>

      <!-- ============ MODO COMPLETO ============ -->
      <template v-else>
        <!-- Paso 1: Nave y origen -->
        <template v-if="stepIndex === 0">
          <section class="new-ticket-panel__section">
            <span class="new-ticket-panel__section-title">Nave e inquilino</span>
            <PlotTenantSearch v-model="selectedPlot" :park-id="parkId" />

            <div v-if="selectedPlot && plotHistory.length" class="new-ticket-panel__history-card">
              <span class="new-ticket-panel__history-title">Últimos tickets de esta nave</span>
              <div v-for="h in plotHistory.slice(0, 3)" :key="h.code" class="new-ticket-panel__history-row">
                <span class="mdi" :class="h.resolved ? 'mdi-check-circle' : 'mdi-circle-outline'"></span>
                {{ h.code }} · {{ typeLabel(h.type) }}
              </div>
            </div>
          </section>

          <section class="new-ticket-panel__section">
            <span class="new-ticket-panel__section-title">Origen del reporte</span>
            <div class="new-ticket-panel__pills">
              <button v-for="o in ORIGIN_OPTIONS" :key="o.value" class="new-ticket-panel__pill" :class="{ 'new-ticket-panel__pill--active': originChannel === o.value }" @click="originChannel = o.value">
                {{ o.label }}
              </button>
            </div>
          </section>

          <section class="new-ticket-panel__section">
            <span class="new-ticket-panel__section-title">Tipo</span>
            <div class="new-ticket-panel__pills">
              <button v-for="t in TYPE_OPTIONS" :key="t.value" class="new-ticket-panel__pill" :class="{ 'new-ticket-panel__pill--active': type === t.value }" @click="type = t.value">
                {{ t.label }}
              </button>
            </div>

            <div v-if="type === 'PREVENTIVE'" class="new-ticket-panel__disabled-hint">
        <span class="new-ticket-panel__field-label">Mantenimiento programado vinculado</span>
        <select v-model="scheduledMaintenanceId" class="new-ticket-panel__input" :disabled="!selectedPlot" @change="onScheduledChosen">
          <option :value="null">{{ selectedPlot ? 'Sin vincular' : 'Elige primero una nave' }}</option>
          <option v-for="o in scheduledOptions" :key="o.id" :value="o.id">{{ scheduledOptionLabel(o) }}</option>
        </select>
        <span class="new-ticket-panel__hint-text">
          {{ selectedPlot && !scheduledOptions.length ? 'No hay mantenimientos programados pendientes para esta nave.' : 'Al completar el mantenimiento, este ticket se resuelve automáticamente.' }}
        </span>
      </div>

            <div v-if="type === 'IMPROVEMENT'" class="new-ticket-panel__approval-toggle">
              <label><input type="checkbox" v-model="requiresApproval" /> Aprobación requerida antes de proceder</label>
            </div>
          </section>
        </template>

        <!-- Paso 2: Descripción -->
        <template v-if="stepIndex === 1">
          <section class="new-ticket-panel__section">
            <span class="new-ticket-panel__section-title">Descripción del problema</span>
            <textarea v-model="description" class="new-ticket-panel__textarea" rows="5" :placeholder="descriptionPlaceholder"></textarea>
          </section>

          <section class="new-ticket-panel__section">
            <span class="new-ticket-panel__section-title">Adjuntos</span>
            <div class="new-ticket-panel__dropzone" @click="fullFileEl?.click()">
              <span class="mdi mdi-cloud-upload-outline"></span> Arrastra fotos o documentos aquí
            </div>
            <input ref="fullFileEl" type="file" multiple style="display: none" @change="onFilesSelected" />
            <div v-if="photos.length" class="new-ticket-panel__photo-previews new-ticket-panel__photo-previews--lg">
              <div v-for="(p, i) in photos" :key="i" class="new-ticket-panel__photo-preview new-ticket-panel__photo-preview--lg">
                <img v-if="p.previewUrl" :src="p.previewUrl" />
                <span v-else class="mdi mdi-file-outline"></span>
                <span class="new-ticket-panel__photo-name">{{ p.file.name }}</span>
                <button class="new-ticket-panel__photo-remove" @click="photos.splice(i, 1)"><span class="mdi mdi-close"></span></button>
              </div>
            </div>
          </section>

          <section class="new-ticket-panel__section">
            <span class="new-ticket-panel__section-title">Ubicación exacta</span>
            <input v-model="locationDetail" class="new-ticket-panel__input" placeholder="Ubicación específica dentro de la nave..." />
          </section>

          <section class="new-ticket-panel__section">
            <span class="new-ticket-panel__section-title">Impacto operativo</span>
            <select v-model="operationalImpact" class="new-ticket-panel__input">
              <option value="">Selecciona una opción</option>
              <option value="NONE">Sin impacto</option>
              <option value="MINOR">Impacto menor</option>
              <option value="MAJOR">Impacto mayor</option>
              <option value="TOTAL_STOP">Detención total</option>
            </select>

            <div v-if="suggestedPriority" class="new-ticket-panel__suggestion">
              Basado en el impacto reportado, sugerimos prioridad {{ priorityLabel(suggestedPriority) }}.
              <button class="new-ticket-panel__apply-btn" @click="priority = suggestedPriority">Aplicar</button>
            </div>
          </section>
        </template>

        <!-- Paso 3: Asignación y prioridad -->
        <template v-if="stepIndex === 2">
          <section class="new-ticket-panel__section">
            <span class="new-ticket-panel__section-title">Prioridad</span>
            <div class="new-ticket-panel__priority-cards">
              <button v-for="p in PRIORITY_OPTIONS" :key="p.value" class="new-ticket-panel__priority-card" :class="{ 'new-ticket-panel__priority-card--active': priority === p.value }" :style="{ borderColor: priority === p.value ? p.color : '#e2e8f0' }" @click="priority = p.value">
                <span class="new-ticket-panel__priority-card-label" :style="{ color: p.color }">{{ p.label }}</span>
                <span class="new-ticket-panel__priority-card-desc">{{ p.desc }}</span>
              </button>
            </div>
          </section>

          <section class="new-ticket-panel__section">
            <span class="new-ticket-panel__section-title">Fecha límite</span>
            <div class="new-ticket-panel__pills">
              <button class="new-ticket-panel__pill" :class="{ 'new-ticket-panel__pill--active': dueOption === 'today' }" @click="setDueOption('today')">Hoy</button>
              <button class="new-ticket-panel__pill" :class="{ 'new-ticket-panel__pill--active': dueOption === 'tomorrow' }" @click="setDueOption('tomorrow')">Mañana</button>
              <button class="new-ticket-panel__pill" :class="{ 'new-ticket-panel__pill--active': dueOption === 'week' }" @click="setDueOption('week')">Esta semana</button>
              <button class="new-ticket-panel__pill" :class="{ 'new-ticket-panel__pill--active': dueOption === 'custom' }" @click="dueOption = 'custom'">Personalizada</button>
            </div>
            <input v-if="dueOption === 'custom'" v-model="slaDueAt" type="datetime-local" class="new-ticket-panel__input" />
          </section>

          <section class="new-ticket-panel__section">
            <span class="new-ticket-panel__section-title">Técnico</span>
            <TechnicianSelectorFull v-model="technicianId" :options="technicianOptions" />
          </section>

          <section class="new-ticket-panel__section">
            <div class="new-ticket-panel__row-2col">
              <div>
                <span class="new-ticket-panel__section-title">Duración estimada</span>
                <div class="new-ticket-panel__duration-row">
                  <input v-model.number="estimatedDurationHours" type="number" class="new-ticket-panel__input" />
                  <select v-model="durationUnit" class="new-ticket-panel__input">
                    <option value="hours">Horas</option>
                    <option value="days">Días</option>
                  </select>
                </div>
              </div>
              <div>
                <span class="new-ticket-panel__section-title">Costo estimado (opcional)</span>
                <input v-model.number="estimatedCost" type="number" class="new-ticket-panel__input" />
              </div>
            </div>
            <div v-if="estimatedCost" class="new-ticket-panel__approval-toggle">
              <label><input type="checkbox" v-model="requiresApproval" /> Requiere aprobación del inquilino</label>
            </div>
          </section>
        </template>

        <!-- Paso 4: Revisión -->
        <template v-if="stepIndex === 3">
          <div class="new-ticket-panel__review-grid">
            <div class="new-ticket-panel__review-col">
              <span class="new-ticket-panel__section-title">Nave e inquilino</span>
              <div>{{ selectedPlot?.tenantName || '—' }}</div>
              <div>{{ selectedPlot?.plotName }}</div>
              <div v-if="selectedPlot?.operationalContact">{{ selectedPlot.operationalContact.name }} · {{ selectedPlot.operationalContact.phone }}</div>
              <div class="new-ticket-panel__review-sub">{{ typeLabel(type) }} · {{ originLabel(originChannel) }}</div>
            </div>

            <div class="new-ticket-panel__review-col">
              <span class="new-ticket-panel__section-title">Descripción</span>
              <div>{{ description || '—' }}</div>
              <div v-if="locationDetail" class="new-ticket-panel__review-sub">{{ locationDetail }}</div>
              <div v-if="operationalImpact" class="new-ticket-panel__review-sub">{{ impactLabel(operationalImpact) }}</div>
              <div v-if="photos.length" class="new-ticket-panel__review-sub">{{ photos.length }} archivo(s) adjunto(s)</div>
            </div>

            <div class="new-ticket-panel__review-col">
              <span class="new-ticket-panel__section-title">Asignación</span>
              <div :style="{ color: PRIORITY_OPTIONS.find(p => p.value === priority)?.color }">{{ priorityLabel(priority) }}</div>
              <div v-if="slaDueAt" class="new-ticket-panel__review-sub">Vence: {{ formatDate(slaDueAt) }}</div>
              <div v-if="selectedTechnician" class="new-ticket-panel__review-sub">{{ selectedTechnician.name }}</div>
              <div v-if="estimatedDurationHours" class="new-ticket-panel__review-sub">{{ estimatedDurationHours }} {{ durationUnit === 'hours' ? 'horas' : 'días' }} estimadas</div>
              <div v-if="estimatedCost" class="new-ticket-panel__review-sub">{{ formatCurrency(estimatedCost) }} estimado</div>
            </div>
          </div>

          <p class="new-ticket-panel__confirm-text">
            Al crear esta solicitud se notificará{{ selectedTechnician ? ` a ${selectedTechnician.name}` : ' al técnico asignado' }} por correo y en la plataforma, y se notificará al inquilino que su solicitud fue registrada.
          </p>
        </template>
      </template>
    </div>

    <div class="new-ticket-panel__footer">
      <template v-if="mode === 'quick'">
        <button class="new-ticket-panel__submit-btn" :disabled="!canSubmitQuick || submitting" @click="onSubmit">
          {{ submitting ? 'Creando...' : 'Crear solicitud' }}
        </button>
      </template>
      <template v-else>
        <button v-if="stepIndex > 0" class="new-ticket-panel__prev-btn" @click="stepIndex--">Anterior</button>
        <div class="new-ticket-panel__footer-spacer"></div>
        <button v-if="stepIndex < 3" class="new-ticket-panel__next-btn" :disabled="!canAdvance" @click="stepIndex++">Siguiente</button>
        <button v-else class="new-ticket-panel__submit-btn" :disabled="submitting" @click="onSubmit">
          {{ submitting ? 'Creando...' : 'Crear solicitud' }}
        </button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import {
  getTechnicianOptions,
  getPlotTicketHistory,
  createTicket,
  uploadTicketPhoto,
  getScheduledMaintenanceOptions,
  searchPlotsForTicket,
} from '@/services/maintenance'
import PlotTenantSearch from './PlotTenantSearch.vue'
import TechnicianChipRow from './TechnicianChipRow.vue'
import TechnicianSelectorFull from './TechnicianSelectorFull.vue'

const props = defineProps({
  parkId: { type: String, required: true },
  initialStatus: { type: String, default: null },
  // Nave con la que se abre el formulario (por ejemplo, desde un contrato). Se busca por su nombre y se selecciona.
  initialPlot: { type: Object, default: null },
})

const emit = defineEmits(['close', 'created'])

const STEPS = [
  { key: 'plot', label: 'Nave y origen' },
  { key: 'desc', label: 'Descripción' },
  { key: 'assign', label: 'Asignación' },
  { key: 'review', label: 'Revisión' },
]

const PRIORITY_OPTIONS = [
  { value: 'CRITICAL', label: 'Crítica', color: '#e84545', desc: 'Detiene la operación del inquilino o representa riesgo de seguridad.' },
  { value: 'HIGH', label: 'Alta', color: '#f97316', desc: 'Afecta significativamente la operación pero no la detiene.' },
  { value: 'MEDIUM', label: 'Media', color: '#f0a500', desc: 'Puede esperar uno o dos días sin impacto grave.' },
  { value: 'LOW', label: 'Baja', color: '#94a3b8', desc: 'Mejora o problema menor sin urgencia.' },
]

const TYPE_OPTIONS = [
  { value: 'CORRECTIVE', label: 'Correctivo' },
  { value: 'PREVENTIVE', label: 'Preventivo' },
  { value: 'IMPROVEMENT', label: 'Mejora' },
]

const ORIGIN_OPTIONS = [
  { value: 'TENANT_PORTAL', label: 'Portal del inquilino' },
  { value: 'PHONE_MESSAGE', label: 'Llamada o mensaje' },
  { value: 'INTERNAL_INSPECTION', label: 'Inspección propia' },
]

const mode = ref('quick')
const stepIndex = ref(0)
const errorMsg = ref('')
const submitting = ref(false)

const selectedPlot = ref(null)
const description = ref('')
const priority = ref('')
const type = ref('CORRECTIVE')
const technicianId = ref(null)
const photos = ref([])

const originChannel = ref('')
const locationDetail = ref('')
const operationalImpact = ref('')
const requiresApproval = ref(false)
const dueOption = ref(null)
const slaDueAt = ref('')
const estimatedDurationHours = ref(null)
const durationUnit = ref('hours')
const estimatedCost = ref(null)

const technicianOptions = ref([])
const plotHistory = ref([])

onMounted(async () => {
  technicianOptions.value = await getTechnicianOptions(props.parkId)
  if (props.initialPlot?.name) {
    try {
      const results = await searchPlotsForTicket(props.parkId, props.initialPlot.name)
      selectedPlot.value = results.find((r) => r.plotId === props.initialPlot.id) || null
    } catch {
      // Sin la nave preseleccionada el usuario la busca a mano.
    }
  }
})

watch(selectedPlot, async (plot) => {
  if (plot?.plotId) plotHistory.value = (await getPlotTicketHistory(plot.plotId)).plotHistory || []
})

// Mantenimientos programados pendientes que se pueden vincular a un ticket preventivo.
const scheduledMaintenanceId = ref(null)
const scheduledOptions = ref([])

async function loadScheduledOptions() {
  scheduledMaintenanceId.value = null
  scheduledOptions.value = []
  if (type.value !== 'PREVENTIVE' || !selectedPlot.value?.plotId) return
  try {
    scheduledOptions.value = await getScheduledMaintenanceOptions(props.parkId, selectedPlot.value.plotId)
  } catch {
    scheduledOptions.value = []
  }
}
watch([type, () => selectedPlot.value?.plotId], loadScheduledOptions)

function scheduledOptionLabel(o) {
  const date = new Date(o.startAt).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
  return `${o.title} · ${date}`
}

// Al elegir uno se precargan descripción y técnico (sin pisar lo que el usuario ya escribió).
function onScheduledChosen() {
  const o = scheduledOptions.value.find((x) => x.id === scheduledMaintenanceId.value)
  if (!o) return
  if (!description.value.trim()) description.value = [o.title, o.description].filter(Boolean).join(' — ')
  if (!technicianId.value && o.technicianId) technicianId.value = o.technicianId
}

const quickFileEl = ref(null)
const fullFileEl = ref(null)

function onFilesSelected(e) {
  const files = Array.from(e.target.files || [])
  files.forEach((file) => {
    photos.value.push({ file, previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null })
  })
  e.target.value = ''
}

function pillStyle(color, active) {
  return active ? { background: color + '26', borderColor: color, color } : {}
}

const descriptionPlaceholder = computed(() => {
  if (type.value === 'PREVENTIVE') return 'Describe el trabajo de mantenimiento a realizar y el alcance esperado...'
  if (type.value === 'IMPROVEMENT') return 'Describe la mejora solicitada y el beneficio esperado...'
  return 'Describe el problema, cuándo empezó, y cómo afecta la operación del inquilino...'
})

const suggestedPriority = computed(() => {
  const map = { NONE: 'LOW', MINOR: 'MEDIUM', MAJOR: 'HIGH', TOTAL_STOP: 'CRITICAL' }
  return operationalImpact.value ? map[operationalImpact.value] : null
})

function setDueOption(opt) {
  dueOption.value = opt
  const now = new Date()
  if (opt === 'today') { now.setHours(23, 59, 0, 0); slaDueAt.value = toLocalInput(now) }
  else if (opt === 'tomorrow') { now.setDate(now.getDate() + 1); now.setHours(23, 59, 0, 0); slaDueAt.value = toLocalInput(now) }
  else if (opt === 'week') {
    const day = now.getDay()
    const diff = (5 - day + 7) % 7
    now.setDate(now.getDate() + diff)
    now.setHours(23, 59, 0, 0)
    slaDueAt.value = toLocalInput(now)
  }
}

function toLocalInput(date) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const selectedTechnician = computed(() => technicianOptions.value.find((t) => t.id === technicianId.value) || null)

const canSubmitQuick = computed(() => !!selectedPlot.value && !!description.value.trim() && !!priority.value)
const canAdvance = computed(() => {
  if (stepIndex.value === 0) return !!selectedPlot.value
  if (stepIndex.value === 1) return !!description.value.trim()
  if (stepIndex.value === 2) return !!priority.value
  return true
})

function typeLabel(t) { return { CORRECTIVE: 'Correctivo', PREVENTIVE: 'Preventivo', IMPROVEMENT: 'Mejora' }[t] || t }
function priorityLabel(p) { return PRIORITY_OPTIONS.find((o) => o.value === p)?.label || p }
function originLabel(o) { return ORIGIN_OPTIONS.find((x) => x.value === o)?.label || '—' }
function impactLabel(i) { return { NONE: 'Sin impacto', MINOR: 'Impacto menor', MAJOR: 'Impacto mayor', TOTAL_STOP: 'Detención total' }[i] || i }
function formatDate(iso) { return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) }
function formatCurrency(n) { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n) }

function onClose() {
  if (description.value || selectedPlot.value) {
    if (!confirm('¿Descartar esta solicitud? Se perderá lo capturado.')) return
  }
  emit('close')
}

async function onSubmit() {
  errorMsg.value = ''
  submitting.value = true
  try {
    const payload = {
        plotId: selectedPlot.value.plotId,
        title: description.value.slice(0, 80),
        description: description.value,
        priority: priority.value,
        type: type.value,
        technicianId: technicianId.value,
        originChannel: originChannel.value || null,
        locationDetail: locationDetail.value || null,
        operationalImpact: operationalImpact.value || null,
        estimatedDurationHours: estimatedDurationHours.value
          ? (durationUnit.value === 'days' ? estimatedDurationHours.value * 24 : estimatedDurationHours.value)
          : null,
        estimatedCost: estimatedCost.value || null,
        requiresApproval: requiresApproval.value,
        slaDueAt: slaDueAt.value || null,
        initialStatus: props.initialStatus,
        scheduledMaintenanceId: type.value === 'PREVENTIVE' ? scheduledMaintenanceId.value : null,
      }

    const ticket = await createTicket(payload)

    for (const p of photos.value) {
      await uploadTicketPhoto(ticket.id, p.file)
    }

    emit('created', ticket)
  } catch (error) {
    errorMsg.value = error.message
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.new-ticket-panel {
  flex: 0 0 40%;
  max-width: 40%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid var(--border-active);
  border-radius: 10px;
  overflow: hidden;
  margin: 0.75rem 1.5rem 1.5rem 0;
  min-height: 0;
}

.new-ticket-panel__header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.25rem;
  border-bottom: 1px solid #f1f5f9;
}

.new-ticket-panel__title {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 16px;
  color: #1e3a5f;
}

.new-ticket-panel__mode-toggle {
  margin-left: auto;
  display: flex;
  gap: 0.25rem;
}

.new-ticket-panel__mode-btn {
  background: #f8f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.3rem 0.6rem;
  font-family: var(--font-body);
  font-size: 11px;
  color: #64748b;
  cursor: pointer;
}

.new-ticket-panel__mode-btn--active {
  background: #1e3a5f;
  border-color: #1e3a5f;
  color: #ffffff;
}

.new-ticket-panel__close-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
}

.new-ticket-panel__steps {
  display: flex;
  align-items: center;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid #f1f5f9;
}

.new-ticket-panel__step {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex: 1;
}

.new-ticket-panel__step-circle {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  color: #94a3b8;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: default;
}

.new-ticket-panel__step--active .new-ticket-panel__step-circle {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #ffffff;
}

.new-ticket-panel__step--done .new-ticket-panel__step-circle {
  background: #dcfce7;
  border-color: #16a34a;
  color: #16a34a;
  cursor: pointer;
}

.new-ticket-panel__step-label {
  font-family: var(--font-body);
  font-size: 10px;
  color: #94a3b8;
  white-space: nowrap;
}

.new-ticket-panel__step--active .new-ticket-panel__step-label {
  color: #1e3a5f;
  font-weight: 500;
}

.new-ticket-panel__step-line {
  flex: 1;
  height: 1px;
  background: #e2e8f0;
  margin: 0 0.3rem;
}

.new-ticket-panel__content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem 1.25rem;
}

.new-ticket-panel__content::-webkit-scrollbar { width: 6px; }
.new-ticket-panel__content::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.new-ticket-panel__content::-webkit-scrollbar-track { background: transparent; }

.new-ticket-panel__error {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  font-family: var(--font-body);
  font-size: 12px;
  margin-bottom: 1rem;
}

.new-ticket-panel__section {
  margin-bottom: 1.1rem;
}

.new-ticket-panel__section-title {
  display: block;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94a3b8;
  margin-bottom: 0.4rem;
}

.new-ticket-panel__row-2col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.new-ticket-panel__textarea,
.new-ticket-panel__input {
  width: 100%;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--text-content);
  background: #f8f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.5rem 0.6rem;
  box-sizing: border-box;
  resize: vertical;
}

.new-ticket-panel__attach-btn {
  background: none;
  border: none;
  color: #3b82f6;
  font-family: var(--font-body);
  font-size: 12px;
  cursor: pointer;
  margin-top: 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0;
}

.new-ticket-panel__dropzone {
  border: 1px dashed #cbd5e1;
  background: #f8f9fc;
  border-radius: 8px;
  padding: 1.2rem;
  text-align: center;
  font-family: var(--font-body);
  font-size: 12px;
  color: #94a3b8;
  cursor: pointer;
}

.new-ticket-panel__photo-previews {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
}

.new-ticket-panel__photo-preview {
  position: relative;
  width: 48px;
  height: 48px;
}

.new-ticket-panel__photo-preview--lg {
  width: 64px;
  height: 64px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.new-ticket-panel__photo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
}

.new-ticket-panel__photo-name {
  font-size: 8px;
  color: #64748b;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.new-ticket-panel__photo-remove {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #dc2626;
  color: #ffffff;
  border: none;
  font-size: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.new-ticket-panel__pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.new-ticket-panel__pill {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 0.3rem 0.7rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: #64748b;
  cursor: pointer;
}

.new-ticket-panel__pill--active {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #3b82f6;
  font-weight: 500;
}

.new-ticket-panel__disabled-hint {
  margin-top: 0.6rem;
}

.new-ticket-panel__field-label {
  display: block;
  font-family: var(--font-body);
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 0.2rem;
}

.new-ticket-panel__hint-text {
  display: block;
  font-family: var(--font-body);
  font-size: 10px;
  color: #94a3b8;
  margin-top: 0.2rem;
  font-style: italic;
}

.new-ticket-panel__approval-toggle {
  margin-top: 0.6rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: #1e3a5f;
}

.new-ticket-panel__history-card {
  background: #f8f9fc;
  border-radius: 6px;
  padding: 0.5rem 0.6rem;
  margin-top: 0.5rem;
  min-height: 80px;
  box-sizing: border-box;
}

.new-ticket-panel__history-title {
  display: block;
  font-family: var(--font-body);
  font-size: 10px;
  color: #94a3b8;
  margin-bottom: 0.3rem;
}

.new-ticket-panel__history-row {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-family: var(--font-body);
  font-size: 11px;
  color: #64748b;
  height: 20px;
}

.new-ticket-panel__suggestion {
  background: #eff6ff;
  border-radius: 6px;
  padding: 0.5rem 0.6rem;
  font-family: var(--font-body);
  font-size: 11px;
  color: #1e40af;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.new-ticket-panel__apply-btn {
  background: none;
  border: none;
  color: #3b82f6;
  font-weight: 500;
  cursor: pointer;
  font-size: 11px;
}

.new-ticket-panel__priority-cards {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.new-ticket-panel__priority-card {
  text-align: left;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.6rem;
  cursor: pointer;
}

.new-ticket-panel__priority-card--active {
  background: #f8f9fc;
}

.new-ticket-panel__priority-card-label {
  display: block;
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 13px;
}

.new-ticket-panel__priority-card-desc {
  display: block;
  font-family: var(--font-body);
  font-size: 11px;
  color: #64748b;
  margin-top: 0.15rem;
}

.new-ticket-panel__duration-row {
  display: flex;
  gap: 0.4rem;
}

.new-ticket-panel__review-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: #1e3a5f;
}

.new-ticket-panel__review-col > div {
  margin-bottom: 0.2rem;
}

.new-ticket-panel__review-sub {
  color: #64748b;
  font-size: 11px;
}

.new-ticket-panel__confirm-text {
  font-family: var(--font-body);
  font-size: 11px;
  color: #64748b;
  margin-top: 1rem;
}

.new-ticket-panel__footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 1.25rem;
  border-top: 1px solid #e2e8f0;
}

.new-ticket-panel__footer-spacer {
  flex: 1;
}

.new-ticket-panel__prev-btn {
  background: none;
  border: none;
  color: #3b82f6;
  font-family: var(--font-body);
  font-size: 13px;
  cursor: pointer;
}

.new-ticket-panel__next-btn,
.new-ticket-panel__submit-btn {
  background: #3b82f6;
  border: none;
  border-radius: 6px;
  padding: 0.6rem 1.2rem;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  color: #ffffff;
  cursor: pointer;
}

.new-ticket-panel__submit-btn {
  width: 100%;
}

.new-ticket-panel__next-btn:disabled,
.new-ticket-panel__submit-btn:disabled {
  opacity: 0.4;
  cursor: default;
}
</style>